import { useQuery } from 'react-query';
import { AxiosResponse } from 'axios';
import apiClient from '../apiClient/apiClient';
import {
  DashboardMetrics, NullSpecsData, FailedExtracts, EventTranslationStatus, NegativePriceData,
  DashboardFilter
} from './dashboard.types';
import { Organization } from '../menu/menu.types';

export const useDashboard = (requestParams: {
  dayFilter: string,
  from: string | null | undefined,
  to: string | null | undefined
}) => {
  return useQuery<AxiosResponse<DashboardMetrics>>({
    queryKey: ['dashboard', requestParams.from, requestParams.to],
    queryFn: () => apiClient.get<DashboardMetrics>(
        '/dashboard', { params: { from: requestParams.from, to: requestParams.to } }),
    enabled: true,
    cacheTime: 0,
    refetchInterval: requestParams.dayFilter === 'TODAY' ? 12000 : false
  });
};

export const useNullSpecsDetails = (
    requestParams: {
      from: string | null | undefined,
      to: string | null | undefined
    },
    enabled: boolean = false
) => {
  return useQuery<AxiosResponse<NullSpecsData[]>>({
    queryKey: ['nullSpecMetrics', requestParams.from, requestParams.to],
    queryFn: () => apiClient.get<NullSpecsData[]>('/dashboard/missing-specs', { params: requestParams }),
    enabled: enabled,
    cacheTime: 0
  });
};

export const useExtractionsAndActivationsDetails = (
    jobId: string,
    extractType: string,
    enabled: boolean = false
) => {
  return useQuery<AxiosResponse<FailedExtracts[]>>({
    queryKey: ['extractions-and-activations-details', jobId, extractType],
    queryFn: () => apiClient.get<FailedExtracts[]>(`/dashboard/failed-extracts/${jobId}?extractType=${extractType}`),
    enabled
  })
};

export const useMenu99PricingDetails = (
    requestParams: {
      from: string | null | undefined,
      to: string | null | undefined
    },
    enabled: boolean = true
) => {
  return useQuery<AxiosResponse<Organization[]>>({
    queryKey: ['menuPricingMetrics', requestParams.from, requestParams.to],
    queryFn: () => apiClient.get<Organization[]>('/dashboard/menus-99-pricing', { params: requestParams }),
    enabled: enabled,
    cacheTime: 0
  });
};

export const useMenuBundlePricingDetails = (
    requestParams: {
      from: string | null | undefined,
      to: string | null | undefined
    },
    enabled: boolean = true
) => {
  return useQuery<AxiosResponse<Organization[]>>({
    queryKey: ['menuBundlePricingMetrics', requestParams.from, requestParams.to],
    queryFn: () => apiClient.get<Organization[]>('/dashboard/bundles-99-pricing', { params: requestParams }),
    enabled: enabled,
    cacheTime: 0
  });
}

export const useMenuNegativePricingDetails = (
    requestParams: {
      from: string | null | undefined,
      to: string | null | undefined
    },
    enabled: boolean = true
) => {
  return useQuery<AxiosResponse<NegativePriceData[]>>({
    queryKey: ['menuNegativePricingMetrics', requestParams.from, requestParams.to],
    queryFn: () => apiClient.get<NegativePriceData[]>('/dashboard/negative-prices', { params: requestParams }),
    enabled: enabled,
    cacheTime: 0
  });
}

const filterParams = (filter: DashboardFilter) => {
  return {
    ...(filter.from && { from: filter.from.toISOString()}),
    ...(filter.to && { to: filter.to.toISOString()}),
  }
}

export const useGetMenuTranslations = (
    filter: DashboardFilter,
    enabled: boolean = true
) => {
  return useQuery<AxiosResponse<EventTranslationStatus[]>>({
    queryKey: ['getMenuTranslations', filter.from, filter.to],
    queryFn: () => apiClient.get<EventTranslationStatus[]>(
        '/dashboard/translations',
        {
          params: {
            ...filterParams(filter),
            menus: true
          }
        }
    ),
    enabled: enabled
  })
}

export const useGetFailedMenuTranslations = (
    filter: DashboardFilter,
    enabled: boolean = true
) => {
  return useQuery<AxiosResponse<EventTranslationStatus[]>>({
    queryKey: ['getFailedMenuTranslations', filter.from, filter.to],
    queryFn: () => apiClient.get<EventTranslationStatus[]>(
        '/dashboard/translations',
        {
          params: {
            ...filterParams(filter),
            menus: true,
            failed: true
          }
        }
    ),
    enabled: enabled,
    cacheTime: 0
  });
};

export const useGetPromotionTranslations = (
    filter: DashboardFilter,
    enabled: boolean = true
) => {
  return useQuery<AxiosResponse<EventTranslationStatus[]>>({
    queryKey: ['getPromotionTranslations', filter.from, filter.to],
    queryFn: () => apiClient.get<EventTranslationStatus[]>(
        '/dashboard/translations',
        {
          params: {
            ...filterParams(filter),
            promotions: true
          }
        }
    ),
    enabled: enabled
  })
}

export const useGetFailedPromotionTranslations = (
    filter: DashboardFilter,
    enabled: boolean = true
) => {
  return useQuery<AxiosResponse<EventTranslationStatus[]>>({
    queryKey: ['getFailedPromotionTranslations', filter.from, filter.to],
    queryFn: () => apiClient.get<EventTranslationStatus[]>(
        '/dashboard/translations',
        {
          params: {
            ...filterParams(filter),
            promotions: true,
            failed: true
          }
        }
    ),
    enabled: enabled,
    cacheTime: 0
  });
};

export const useGetTranslations = (
    filter: DashboardFilter,
    enabled: boolean = true
) => {
  return useQuery<AxiosResponse<EventTranslationStatus[]>>({
    queryKey: ['getTranslations', filter.from, filter.to],
    queryFn: () => apiClient.get<EventTranslationStatus[]>(
        '/dashboard/translations',
        { params: { ...filterParams(filter) } }
    ),
    enabled: enabled
  })
}

export const useGetFailedTranslations = (
    filter: DashboardFilter,
    enabled: boolean = true
) => {
  return useQuery<AxiosResponse<EventTranslationStatus[]>>({
    queryKey: ['getFailedTranslations', filter],
    queryFn: () => apiClient.get<EventTranslationStatus[]>(
        '/dashboard/translations',
        {
          params: {
            ...filterParams(filter),
            failed: true
          }
        }
    ),
    enabled: enabled,
    cacheTime: 0
  });
};

export const useGetTranslationLogs = (
    filter: DashboardFilter,
    enabled: boolean = true
) => {
  return useQuery<AxiosResponse<EventTranslationStatus[]>>({
    queryKey: ['getTranslationLogs', filter],
    queryFn: () => apiClient.get<EventTranslationStatus[]>(
        '/dashboard/translations/logs',
        { params: { ...filterParams(filter) } }
    ),
    enabled: enabled
  })
}